import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Card,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  PersonRemove as RemoveFriendIcon,
  PersonAdd as AddFriendIcon,
  Visibility as ViewProfileIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Key as KeyIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import FriendsManager, { Friend, FriendInvitation } from '../utils/FriendsManager';
import { fetchAuthSession } from 'aws-amplify/auth';
import SendFriendInviteDialog from '../components/SendFriendInviteDialog';
import { buildApiEndpoint } from '../configuration/api';

const Friends: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuthGen2();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<FriendInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removeFriendDialogOpen, setRemoveFriendDialogOpen] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState<Friend | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    // Don't do anything if still loading auth
    if (authLoading) {
      return;
    }

    if (isAuthenticated) {
      loadFriendsData();
    } else {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  const loadFriendsData = async () => {
    if (!isAuthenticated) {
      setError('You must be logged in to view friends');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Load friends, pending invitations, and user settings in parallel
      const [friendsData, invitationsData] = await Promise.all([
        FriendsManager.getFriends(token),
        FriendsManager.getPendingInvitations(token)
      ]);

      setFriends(friendsData);
      setPendingInvitations(invitationsData);

      // Fetch user settings to get invite code
      const settingsUrl = buildApiEndpoint('/user-settings', true);
      const settingsResponse = await fetch(settingsUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings?.inviteCode) {
          setInviteCode(settingsData.settings.inviteCode);
        }
      } else {
        console.warn('Failed to load user settings:', settingsResponse.status);
      }
    } catch (err) {
      console.error('Error loading friends data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load friends data');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (!friendToRemove || !isAuthenticated) return;

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      await FriendsManager.removeFriend(friendToRemove.userId, token);

      // Refresh friends list
      await loadFriendsData();
      setRemoveFriendDialogOpen(false);
      setFriendToRemove(null);
    } catch (err) {
      console.error('Error removing friend:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove friend');
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    if (!isAuthenticated) return;

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      await FriendsManager.acceptInvitation(invitationId, token);

      // Refresh data
      await loadFriendsData();
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    if (!isAuthenticated) return;

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      await FriendsManager.declineInvitation(invitationId, token);

      // Refresh data
      await loadFriendsData();
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleCopyInviteCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode);
      setCopySuccess(true);
    }
  };

  // Show loading state while checking authentication
  if (authLoading || loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center', px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {authLoading ? 'Checking authentication...' : 'Loading friends...'}
        </Typography>
      </Container>
    );
  }

  // Show auth required message if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Alert severity="info">
          Please log in to view your friends and manage friend requests.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Friends
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddFriendIcon />}
          onClick={() => setInviteDialogOpen(true)}
        >
          Invite Friend
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Invite Code Display */}
      {inviteCode && (
        <Card variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <KeyIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Your Invite Code
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: 'monospace', letterSpacing: 2, fontWeight: 'bold' }}>
                  {inviteCode}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Share this code with friends so they can add you!
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Copy invite code">
              <IconButton onClick={handleCopyInviteCode} color="primary">
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Card>
      )}

      {/* Pending Invitations Section */}
      {pendingInvitations.length > 0 && (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Badge badgeContent={pendingInvitations.length} color="primary">
              <NotificationsIcon />
            </Badge>
            <Typography variant="h6" sx={{ ml: 1 }}>
              Pending Invitations
            </Typography>
          </Box>
          <List>
            {pendingInvitations.map((invitation, index) => (
              <React.Fragment key={invitation.invitationId}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <Box>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleAcceptInvitation(invitation.invitationId)}
                        sx={{ mr: 1 }}
                      >
                        Accept
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDeclineInvitation(invitation.invitationId)}
                      >
                        Decline
                      </Button>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <EmailIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${invitation.fromUserName} sent you a friend request`}
                    secondary={new Date(invitation.createdAt).toLocaleDateString()}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Friends List */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          My Friends ({friends.length})
        </Typography>

        {friends.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              You haven't added any friends yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Invite friends to compete and compare stats!
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddFriendIcon />}
              onClick={() => setInviteDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              Invite Your First Friend
            </Button>
          </Box>
        ) : (
          <List>
            {friends.map((friend, index) => (
              <React.Fragment key={friend.userId}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        aria-label="view profile"
                        onClick={() => navigate(`/friends/${friend.userId}`)}
                        sx={{ mr: 1 }}
                      >
                        <ViewProfileIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="remove friend"
                        onClick={() => {
                          setFriendToRemove(friend);
                          setRemoveFriendDialogOpen(true);
                        }}
                      >
                        <RemoveFriendIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {getInitials(friend.displayName)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={friend.displayName}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={`Streak: ${friend.currentStreak}`}
                          size="small"
                          color={friend.currentStreak > 0 ? 'success' : 'default'}
                        />
                        <Chip
                          label={`Win Rate: ${friend.winRate}%`}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          label={`Games: ${friend.totalGamesPlayed}`}
                          size="small"
                        />
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Remove Friend Confirmation Dialog */}
      <Dialog open={removeFriendDialogOpen} onClose={() => setRemoveFriendDialogOpen(false)}>
        <DialogTitle>Remove Friend?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {friendToRemove?.displayName} from your friends list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveFriendDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveFriend} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Friend Dialog */}
      <SendFriendInviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={loadFriendsData}
      />

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Invite code copied to clipboard!"
      />
    </Container>
  );
};

export default Friends;
